import { Button, Drawer, Grid, Icons, Input, message, Space, Typography, useDrawerForm } from '@pankod/refine-antd'
import { useTranslate } from '@pankod/refine-core'
import { definitions } from 'interfaces'
import { useState } from 'react'
import { MAX_ROWS_REFUNDS, SEND_CORRECTION_INVOICES_TEMPLATE_PATH, SEND_REFUND_INVOICES_TEMPLATE_PATH, supabaseClient } from 'utility'
import { read, utils } from 'xlsx'

export const ImportRefundFromExcelButton = ({ isCorrection }: { isCorrection: boolean }) => {
  const t = useTranslate()
  const breakpoint = Grid.useBreakpoint()

  const {
    drawerProps: createDrawerProps,
    show: createShow
  } = useDrawerForm<definitions['orders']>({
    action: 'create',
    redirect: false
  })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [messageApi, contextHolder] = message.useMessage()
  const [updateFile, setUpdateFile] = useState(null)
  const [updateFinished, setUpdateFinished] = useState<string | null>(null)

  const handleFile = (e: any) => {
    setUpdateFile(e.target.files[0])
  }

  const chunkArray = <T, >(items: T[], size: number) => {
    const chunks: T[][] = []
    for (let i = 0; i < items.length; i += size) {
      chunks.push(items.slice(i, i + size))
    }
    return chunks
  }

  const markAsSent = async (data: Record<string, number>) => {
    const refundNumbers = Object.keys(data)
      .map((value) => String(value).trim())
      .filter((value) => value !== '')

    if (refundNumbers.length === 0) {
      return
    }

    const refundChunks = chunkArray(refundNumbers, MAX_ROWS_REFUNDS)

    for (const chunk of refundChunks) {
      // 1) Select rows that have any of the refund numbers in this chunk
      const { data: rows, error: selectError } = await supabaseClient
        .from('refund_invoice_data')
        .select('id,refund_number,order_id')
        .in('refund_number', chunk)

      if (selectError || !rows) {
        messageApi.open({
          type: 'error',
          content: t('notifications.errorMsg')
        })
        return
      }

      // 2) Keep only the rows that match the provided order id for that refund number
      const matchedIds: number[] = []
      rows.forEach((r: any) => {
        const refundNumber = String(r.refund_number).trim()
        const orderId = Number(r.order_id)
        if (data[refundNumber] !== undefined && Number(data[refundNumber]) === orderId) {
          matchedIds.push(r.id)
        }
      })

      if (matchedIds.length === 0) {
        continue
      }

      // 3) Update matched rows by primary key in a single call
      const { error: updateError } = await supabaseClient
        .from('refund_invoice_data')
        .update({ reviewed: true })
        .in('id', matchedIds)

      if (updateError) {
        messageApi.open({
          type: 'error',
          content: t('notifications.errorMsg')
        })
        return
      }
    }

    messageApi.open({
      type: 'success',
      content: t('notifications.successMsg')
    })
    window.location.reload()
  }

  function isValidData (orderId: number): boolean {
    const orderIdString = String(orderId)

    // there is not 0 or negative id
    if (orderId <= 0) return false

    // Order ID must be a number of n digits
    const orderIdRegex = /^\d+$/

    if (!orderIdRegex.test(orderIdString)) {
      return false
    }

    return true
  }

  function isValidFormat (csv: any[][]): boolean {
    return csv.every((row, index) => {
      if (row.length === 0) return true

      // If the row has more than 2 columns, we ignore it. Only can write in the first two columns
      const rest = row.slice(2)
      if (rest.some((cell) => cell !== undefined && String(cell).trim() !== '')) return false

      // Omit first row (header)
      if (index === 0) return true

      return true
    })
  }

  const updateInvoicesExcel = async (e: any) => {
    e.preventDefault()
    setError(false)
    setLoading(true)
    setUpdateFinished(null)

    const reader = new FileReader()
    reader.onload = async function (e) {
      try {
        const data = e?.target?.result
        const readedData = read(new Uint8Array(data as ArrayBuffer), { type: 'array' })
        const wsname = readedData.SheetNames[0]
        const ws = readedData.Sheets[wsname]

        const csvData: any[][] = utils.sheet_to_json(ws, { header: 1 })
        if (isValidFormat(csvData)) {
          const processedData = csvData.reduce((acc: Record<string, number>, row: any[]) => {
            if (row.length >= 2) {
              const refundInvoiceNumber = row[0]
              const orderId = row[1]
              // Normalize and validate order id before adding
              const orderIdNumber = Number(orderId)
              if (isValidData(orderIdNumber)) {
                acc[String(refundInvoiceNumber ?? '').trim()] = orderIdNumber
              }
            }
            return acc
          }, {})
          await markAsSent(processedData)
          setLoading(false)
          setUpdateFile(null)
          setUpdateFinished('ok')
          window.location.reload()
        } else {
          messageApi.open({
            type: 'error',
            content: t('notifications.errorMsg')
          })
          setError(true)
          setLoading(false)
          setUpdateFile(null)
          setUpdateFinished('errorValidate')
        }
      } catch (err) {
        messageApi.open({
          type: 'error',
          content: t('notifications.errorMsg')
        })
        setError(true)
        setLoading(false)
        setUpdateFile(null)
        setUpdateFinished('error')
      }
    }

    if (updateFile) {
      reader.readAsArrayBuffer(updateFile)
    }
  }

  return <>
    {contextHolder}
    <Button type="primary" onClick={() => createShow()}>
      {isCorrection ? t('refunds.importCorrections.title') : t('refunds.importRefunds.title')}
    </Button>
    <Drawer
      {...createDrawerProps}
      width={breakpoint.sm ? '800px' : '100%'}
      bodyStyle={{ padding: 0 }}
      zIndex={1001}
    >
      <Space style={{ width: '100%', display: 'grid', marginTop: '5%', marginLeft: '10%' }} direction="vertical" size={12}>
        <Typography.Text>
          {isCorrection ? t('refunds.importCorrections.title') : t('refunds.importRefunds.title')}
        </Typography.Text>

        <a href={isCorrection ? SEND_CORRECTION_INVOICES_TEMPLATE_PATH : SEND_REFUND_INVOICES_TEMPLATE_PATH} download={isCorrection ? 'send-correction-invoices-template.xlsx' : 'send-refund-invoices-template.xlsx'}>
          <Button>
            <Icons.DownloadOutlined />
            {t('order.biReport.labelTemplate')}
          </Button>
        </a>

        <Typography.Text>{t('marketplace.actions.uploadExcel')}</Typography.Text>
        <Input
          accept='.xlsx'
          bordered={false}
          size='middle'
          type='file'
          key='exceluploader'
          onChange={handleFile}
        />
        <Button disabled={updateFile === null} loading={loading} type='primary'
          onClick={(e: any) => updateInvoicesExcel(e)}
        >
          {isCorrection ? t('refunds.importCorrections.updateCorrections') : t('refunds.importRefunds.updateRefunds')}
        </Button>

        {loading && <Typography style={{ marginTop: '2rem', fontWeight: 600, fontStyle: 'italic' }}>
          {t('order.biReport.labelUpdating')}
        </Typography>}
        {updateFinished && <Typography style={{ marginTop: '2rem', fontWeight: 600, fontStyle: 'italic' }}>
          {isCorrection ? t(`refunds.importCorrections.labelFinish${updateFinished}`) : t(`refunds.importRefunds.labelFinish${updateFinished}`)}
        </Typography>}

      </Space>

      {(error)
        ? <Space style={{ width: '100%', display: 'grid', marginTop: '5%', marginLeft: '10%' }} direction="vertical" size={12}>
          <Typography.Text>
            {isCorrection ? t('refunds.importCorrections.error') : t('refunds.importRefunds.error')}
          </Typography.Text>
        </Space>
        : null}
    </Drawer>
  </>
}
